import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  deleteBooking, getBooking, holdBooking, rebookBooking, requestBookerUpdate, transitionBooking, unholdBooking,
} from "@/lib/api";
import { bookingMissingFields } from "@/lib/constants";

// Run an API call; toast error or success, then refresh. Returns the result data or undefined on error.
const runAndRefresh = async (call, successMsg, refresh) => {
  const { data, error } = await call;
  if (error) { toast.error(error.message); return undefined; }
  toast.success(typeof successMsg === "function" ? successMsg(data) : successMsg);
  refresh();
  return data ?? true;
};

// Row + bulk actions and the dialog state they drive.
export function useBookingActions({ refresh, selection }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);       // booking row OR { __new: true }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [ncrBooking, setNcrBooking] = useState(null);
  const [rebookSource, setRebookSource] = useState(null);

  const edit = async (row) => {
    const { data } = await getBooking(row.id);
    setEditing(data || row);
  };

  const hold = async (row) => {
    const reason = window.prompt(`Put booking "${row.reference_number}" on hold.\nReason (shown to other tenant users):`, "Awaiting information from booker");
    if (!reason) return;
    await runAndRefresh(holdBooking(row.id, reason, user), "Booking on hold.", refresh);
  };

  const unhold = (row) => runAndRefresh(unholdBooking(row.id), "Hold released.", refresh);

  const requestUpdate = async (row) => {
    const missing = bookingMissingFields(row);
    if (missing.length === 0) { toast.info("This booking has no missing fields."); return; }
    const note = window.prompt(
      `Email the booker about missing fields:\n  ${missing.join(", ")}\n\nAdd a custom note (optional):`, ""
    );
    if (note === null) return;
    await runAndRefresh(requestBookerUpdate(row, missing, note), (d) => `Update request queued to ${d.recipient}.`, refresh);
  };

  const submitRebook = async ({ newStart, newEnd, resetDriver }) => {
    if (!rebookSource) return;
    const ok = await runAndRefresh(rebookBooking(rebookSource.id, { newStart, newEnd, resetDriver }),
      (d) => `Rebooked as draft ${d?.reference_number || ""} — opens in edit.`, refresh);
    if (ok) setRebookSource(null);
  };

  const confirmDeleteNow = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    await runAndRefresh(deleteBooking(id), "Booking deleted", refresh);
  };

  const bulk = async (confirmMsg, fn, failMsg, okMsg) => {
    if (!window.confirm(confirmMsg)) return;
    const ids = Array.from(selection.selectedIds);
    const failed = (await Promise.all(ids.map(fn))).filter((r) => r.error).length;
    if (failed) toast.error(failMsg(failed, ids.length)); else toast.success(okMsg(ids.length));
    selection.clear();
    refresh();
  };
  const n = selection.selectedIds.size;
  const bulkDelete = () => bulk(`Delete ${n} booking(s)? This cannot be undone.`, (id) => deleteBooking(id),
    (f, t) => `${f} of ${t} failed to delete.`, (t) => `Deleted ${t} bookings.`);
  const bulkTransition = (toStatus) => bulk(`Set ${n} booking(s) to "${toStatus}"?`,
    (id) => transitionBooking({ bookingId: id, toStatus }),
    (f, t) => `${f} of ${t} failed.`, (t) => `${t} bookings → ${toStatus}.`);

  return {
    editing, setEditing, confirmDelete, setConfirmDelete, ncrBooking, setNcrBooking, rebookSource, setRebookSource,
    edit, hold, unhold, requestUpdate, submitRebook, confirmDeleteNow, bulkDelete, bulkTransition,
  };
}
